import { Mapper } from '@/core/mapper/base-mapper';
import type { GetKimLhcResultResponseDTO } from '@/core/dto/kim-lhc';
import type { KimLhcResult } from '@/types';
import type {
	BodyPosture,
	CargoHandlingCondition,
	WorkOrganization,
	PostureTwist,
	PostureLoadCenter,
	PostureArms,
	PostureHands,
	WorkConditionHandsPosition,
	WorkConditionTransfer,
	WorkConditionSpatialCondition,
	WorkConditionAdverse,
	WorkConditionClothes,
	WorkConditionHoldLoad
} from '@/components/views/Report/KimLhc/enums';

export class GetKimLhcResultMapper implements Mapper<KimLhcResult> {
	constructor(private readonly data: GetKimLhcResultResponseDTO) {}

	toDomain(): KimLhcResult {
		return {
			id: this.data.id,
			information: {
				companyName: this.data.information.company.name ?? '-',
				organizationName: this.data.information.organization.name ?? '-',
				sectorName: this.data.information.sector.name ?? '-',
				lineName: this.data.information.line.name ?? '-',
				workstationName: this.data.information.workstation.name ?? '-',
				activityName: this.data.information.activity.name ?? '-',
				evaluatorName: this.data.information.evaluator.name ?? '-',
				collectDate: this.data.information.collect_date,
				dayDuration: this.data.information.day_duration,
				activityDuration: this.data.information.activity_duration,
				transportingCargoInPairs: this.data.information.transporting_cargo_in_pairs,
				fileName: this.data.information.file.original_name ?? '-',
				createdAt: this.data.information.created_at,
				fileId: this.data.information.file.id ?? '-',
				workstationId: this.data.information.workstation.id ?? '-'
			},
			result: {
				manScore: this.data.result.man.score,
				manRiskLevel: this.data.result.man.risk,
				womanScore: this.data.result.woman.score,
				womanRiskLevel: this.data.result.woman.risk
			},
			categories: {
				frequency: {
					score: this.data.categories.frequency.score,
					value: this.data.categories.frequency.value,
					maxScore: this.data.categories.frequency.max_score
				},
				weight: {
					value: this.data.categories.weight.value,
					effectiveWeight: this.data.categories.weight.effective_weight,
					inclinedWeight: this.data.categories.weight.inclined_weight,
					transportingCargoInPairs: this.data.categories.weight.transporting_cargo_in_pairs,
					maxScore: this.data.categories.weight.max_score,
					manScore: this.data.categories.weight.man_score,
					womanScore: this.data.categories.weight.woman_score
				},
				cargoHandlingCondition: {
					score: this.data.categories.cargo_handling_condition.score,
					value: this.data.categories.cargo_handling_condition.value as CargoHandlingCondition,
					maxScore: this.data.categories.cargo_handling_condition.max_score
				},
				posture: {
					score: this.data.categories.posture.score,
					value: this.data.categories.posture.value as BodyPosture,
					additionalFactors: {
						twist: (this.data.categories.posture.additional_factors.twist as PostureTwist) ?? null,
						loadCenter:
							(this.data.categories.posture.additional_factors.load_center as PostureLoadCenter) ?? null,
						arms: (this.data.categories.posture.additional_factors.arms as PostureArms) ?? null,
						hands: (this.data.categories.posture.additional_factors.hands as PostureHands) ?? null
					},
					maxScore: this.data.categories.posture.max_score
				},
				workCondition: {
					score: this.data.categories.work_condition.score,
					handsPosition:
						(this.data.categories.work_condition
							.work_condition_hands_position as WorkConditionHandsPosition) ?? null,
					transfer:
						(this.data.categories.work_condition.work_condition_transfer as WorkConditionTransfer) ?? null,
					spatialCondition:
						(this.data.categories.work_condition
							.work_condition_spatial_condition as WorkConditionSpatialCondition) ?? null,
					adverse:
						(this.data.categories.work_condition.work_condition_adverse as WorkConditionAdverse) ?? null,
					clothes:
						(this.data.categories.work_condition.work_condition_clothes as WorkConditionClothes) ?? null,
					holdLoad:
						(this.data.categories.work_condition.work_condition_hold_load as WorkConditionHoldLoad) ?? null,
					maxScore: this.data.categories.work_condition.max_score
				},
				workOrganization: {
					score: this.data.categories.work_organization.score,
					value: this.data.categories.work_organization.value as WorkOrganization,
					maxScore: this.data.categories.work_organization.max_score
				}
			},
			comment: this.data.comment,
			consolidated: this.data.consolidated
		};
	}
}
